import { useState } from "react";

interface Props {
  factoryName: string;
  onClose: () => void;
  onDonate: () => void;
}

type Tab = "profile" | "products" | "inbox" | "contact" | "donate";

const mockProducts = [
  { id: 1, name: "Wireless Earbuds TWS Pro", price: "$8.50 FOB", stock: "In Factory", image: "https://public.readdy.ai/ai/img_res/4fd5305d509c3d5f7926e2520c81e061.jpg" },
  { id: 2, name: "LED Strip Light RGB 5050", price: "$3.20 FOB", stock: "Overseas Warehouse", image: "https://readdy.ai/api/search-image?query=LED%20strip%20light%20RGB%20colorful%20product%20photo%20clean%20background%20professional&width=200&height=200&seq=fp2&orientation=squarish" },
  { id: 3, name: "Solar Panel 400W Mono", price: "$95.00 FOB", stock: "In Factory", image: "https://readdy.ai/api/search-image?query=solar%20panel%20monocrystalline%20product%20photo%20clean%20white%20background%20professional&width=200&height=200&seq=fp3&orientation=squarish" },
];

interface Inquiry {
  id: number;
  buyer: string;
  company: string;
  country: string;
  flag: string;
  product: string;
  quantity: string;
  budget: string;
  message: string;
  time: string;
  read: boolean;
  replied: boolean;
  whatsapp: string;
  email: string;
  replies: { from: "factory" | "buyer"; text: string; time: string }[];
}

const mockInquiries: Inquiry[] = [
  {
    id: 1,
    buyer: "James Mitchell",
    company: "TechRetail Inc.",
    country: "USA",
    flag: "🇺🇸",
    product: "Wireless Earbuds TWS Pro",
    quantity: "10,000 units",
    budget: "$80,000 – $100,000",
    message: "Hi! We are interested in your TWS earbuds. Can you provide a sample and your best FOB price? We need CE and FCC certifications. Please share your WhatsApp.",
    time: "2 hours ago",
    read: false,
    replied: false,
    whatsapp: "+1 (555) 234-5678",
    email: "james@techretail.com",
    replies: [],
  },
  {
    id: 2,
    buyer: "Sophie Müller",
    company: "EuroLight GmbH",
    country: "Germany",
    flag: "🇩🇪",
    product: "LED Strip Light RGB 5050",
    quantity: "50,000 rolls",
    budget: "$12,000 – $18,000",
    message: "We need LED strips for a large hotel project in Berlin. CE certified required. Can you ship to our Rotterdam warehouse? What is your lead time?",
    time: "5 hours ago",
    read: false,
    replied: false,
    whatsapp: "+49 170 123 4567",
    email: "sophie@eurolight.de",
    replies: [],
  },
  {
    id: 3,
    buyer: "Raj Sharma",
    company: "SolarTech India",
    country: "India",
    flag: "🇮🇳",
    product: "Solar Panel 400W Mono",
    quantity: "500 units",
    budget: "$45,000 – $55,000",
    message: "We are looking for 400W monocrystalline panels for a commercial rooftop project. Please send your latest price list and technical datasheet.",
    time: "1 day ago",
    read: true,
    replied: true,
    whatsapp: "+91 98765 43210",
    email: "raj@solartech.in",
    replies: [
      { from: "factory", text: "Hello Raj! Thank you for your inquiry. Our FOB price for 500 units is $92/unit. I have sent the datasheet to your email. Please add me on WhatsApp for faster communication.", time: "23 hours ago" },
      { from: "buyer", text: "Thank you! I received the datasheet. Can you do $88/unit for 500 units? We can place the order this week.", time: "20 hours ago" },
    ],
  },
  {
    id: 4,
    buyer: "Amara Osei",
    company: "Lagos Electronics Hub",
    country: "Nigeria",
    flag: "🇳🇬",
    product: "Wireless Earbuds TWS Pro",
    quantity: "5,000 units",
    budget: "$35,000 – $45,000",
    message: "We want to import your earbuds for our retail chain in Lagos. Can you do private label / OEM with our brand logo? What is the MOQ for custom packaging?",
    time: "2 days ago",
    read: true,
    replied: false,
    whatsapp: "+234 803 456 7890",
    email: "amara@lagoselectronics.ng",
    replies: [],
  },
  {
    id: 5,
    buyer: "Chen Wei",
    company: "Pacific Trade Co.",
    country: "Australia",
    flag: "🇦🇺",
    product: "LED Strip Light RGB 5050",
    quantity: "20,000 rolls",
    budget: "$6,000 – $9,000",
    message: "Hi, we need RGB LED strips for our Sydney warehouse. Do you have stock ready to ship? We prefer sea freight to Melbourne port.",
    time: "3 days ago",
    read: true,
    replied: true,
    whatsapp: "+61 412 345 678",
    email: "chen@pacifictrade.com.au",
    replies: [
      { from: "factory", text: "Hi Chen! Yes, we have 30,000 rolls in stock at our overseas warehouse in Sydney. We can ship within 3 days. Price is $0.38/roll for 20,000 units.", time: "3 days ago" },
    ],
  },
  {
    id: 6,
    buyer: "Maria Santos",
    company: "BrightHome Brasil",
    country: "Brazil",
    flag: "🇧🇷",
    product: "LED Strip Light RGB 5050",
    quantity: "8,000 rolls",
    budget: "$2,500 – $4,000",
    message: "Olá! We are a home decor retailer in São Paulo. Interested in your LED strips. Do you accept payment via Alibaba Trade Assurance? What certifications do you have?",
    time: "4 days ago",
    read: true,
    replied: false,
    whatsapp: "+55 11 9 8765 4321",
    email: "maria@brighthome.com.br",
    replies: [],
  },
];

export default function FactoryProfileModal({ factoryName, onClose, onDonate }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>("profile");
  const [editMode, setEditMode] = useState(false);
  const [address, setAddress] = useState("Building 12, Longhua Industrial Park, Shenzhen, Guangdong, China 518109");
  const [warehouseInfo, setWarehouseInfo] = useState("overseas");
  const [whatsapp, setWhatsapp] = useState("+86 138 0013 8000");
  const [contactEmail, setContactEmail] = useState("sales@factory.com");
  const [saved, setSaved] = useState(false);
  const [msgSent, setMsgSent] = useState(false);
  const [message, setMessage] = useState("");
  const [contactMethod, setContactMethod] = useState<"whatsapp" | "email">("whatsapp");

  const [products, setProducts] = useState(mockProducts);
  const [editingProduct, setEditingProduct] = useState<number | null>(null);
  const [editProductName, setEditProductName] = useState("");
  const [editProductPrice, setEditProductPrice] = useState("");
  const [editProductStock, setEditProductStock] = useState("");

  // Inbox state
  const [inquiries, setInquiries] = useState<Inquiry[]>(mockInquiries);
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [replyText, setReplyText] = useState("");
  const [replySent, setReplySent] = useState(false);
  const [inboxFilter, setInboxFilter] = useState<"all" | "unread" | "replied">("all");

  const unreadCount = inquiries.filter(i => !i.read).length;

  const filteredInquiries = inquiries.filter(i => {
    if (inboxFilter === "unread") return !i.read;
    if (inboxFilter === "replied") return i.replied;
    return true;
  });

  const openInquiry = (inq: Inquiry) => {
    setSelectedInquiry(inq);
    setInquiries(prev => prev.map(i => i.id === inq.id ? { ...i, read: true } : i));
    setReplyText("");
    setReplySent(false);
  };

  const sendReply = () => {
    if (!replyText.trim() || !selectedInquiry) return;
    const newReply = { from: "factory" as const, text: replyText, time: "Just now" };
    setInquiries(prev => prev.map(i =>
      i.id === selectedInquiry.id
        ? { ...i, replied: true, replies: [...i.replies, newReply] }
        : i
    ));
    setSelectedInquiry(prev => prev ? { ...prev, replied: true, replies: [...prev.replies, newReply] } : prev);
    setReplyText("");
    setReplySent(true);
    setTimeout(() => setReplySent(false), 3000);
  };

  const handleSaveProfile = () => {
    setSaved(true);
    setEditMode(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleSendMessage = () => {
    if (!message.trim()) return;
    setMsgSent(true);
    setMessage("");
    setTimeout(() => setMsgSent(false), 3000);
  };

  const startEditProduct = (p: typeof mockProducts[0]) => {
    setEditingProduct(p.id);
    setEditProductName(p.name);
    setEditProductPrice(p.price);
    setEditProductStock(p.stock);
  };

  const saveProduct = (id: number) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, name: editProductName, price: editProductPrice, stock: editProductStock } : p));
    setEditingProduct(null);
  };

  const tabs: { key: Tab; label: string; icon: string; badge?: number }[] = [
    { key: "profile", label: "Factory Info", icon: "ri-building-2-line" },
    { key: "products", label: "Products", icon: "ri-box-3-line" },
    { key: "inbox", label: "Inbox", icon: "ri-inbox-line", badge: unreadCount },
    { key: "contact", label: "Contact", icon: "ri-message-3-line" },
    { key: "donate", label: "Donate", icon: "ri-heart-fill" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-400 to-orange-400 px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
              <i className="ri-building-2-line text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-white font-extrabold text-base">{factoryName}</h2>
              <div className="flex items-center gap-1.5">
                <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full font-semibold">Verified Factory</span>
                <span className="bg-green-400/30 text-white text-xs px-2 py-0.5 rounded-full font-semibold">Gold Member</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 shrink-0 overflow-x-auto">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => { setActiveTab(t.key); if (t.key !== "inbox") setSelectedInquiry(null); }}
              className={`relative flex items-center gap-1.5 px-4 py-3 text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer border-b-2 ${activeTab === t.key ? "border-amber-400 text-amber-600" : "border-transparent text-gray-500 hover:text-gray-700"} ${t.key === "donate" ? "text-rose-500 hover:text-rose-600" : ""}`}
            >
              <i className={`${t.icon} text-sm`}></i>
              {t.label}
              {t.badge !== undefined && t.badge > 0 && (
                <span className="absolute -top-0.5 right-1 bg-red-500 text-white text-[9px] font-extrabold w-4 h-4 flex items-center justify-center rounded-full">
                  {t.badge}
                </span>
              )}
              {t.key === "donate" && <span className="bg-rose-100 text-rose-500 text-[10px] px-1.5 py-0.5 rounded-full font-bold">❤️</span>}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* ── Factory Info Tab ── */}
          {activeTab === "profile" && (
            <div className="flex flex-col gap-5">
              {saved && (
                <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill"></i> Factory profile saved successfully!
                </div>
              )}
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-800">Factory Profile</h3>
                <button
                  onClick={() => editMode ? handleSaveProfile() : setEditMode(true)}
                  className={`flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${editMode ? "bg-amber-400 text-white hover:bg-amber-500" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                >
                  <i className={editMode ? "ri-save-line" : "ri-edit-line"}></i>
                  {editMode ? "Save Changes" : "Edit Profile"}
                </button>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 flex items-center gap-1">
                  <i className="ri-map-pin-line text-amber-400"></i> Factory Address
                </label>
                {editMode ? (
                  <textarea value={address} onChange={e => setAddress(e.target.value)} rows={2} className="w-full border border-amber-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 resize-none" />
                ) : (
                  <div className="bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-700">{address}</div>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 flex items-center gap-1">
                  <i className="ri-store-3-line text-amber-400"></i> Product Stock Location
                </label>
                {editMode ? (
                  <div className="flex gap-3">
                    {[
                      { value: "factory", label: "In Factory", icon: "ri-building-2-line", desc: "Products stored at factory" },
                      { value: "overseas", label: "Overseas Warehouse", icon: "ri-global-line", desc: "Ready stock in overseas hub" },
                      { value: "both", label: "Both", icon: "ri-checkbox-multiple-line", desc: "Factory + overseas warehouse" },
                    ].map(opt => (
                      <button key={opt.value} onClick={() => setWarehouseInfo(opt.value)} className={`flex-1 flex flex-col items-center gap-1 p-3 rounded-xl border-2 text-xs font-semibold transition-all cursor-pointer ${warehouseInfo === opt.value ? "border-amber-400 bg-amber-50 text-amber-700" : "border-gray-200 text-gray-500 hover:border-amber-200"}`}>
                        <i className={`${opt.icon} text-lg`}></i>
                        <span>{opt.label}</span>
                        <span className="text-[10px] font-normal text-gray-400 text-center">{opt.desc}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-700 flex items-center gap-2">
                    <i className={`${warehouseInfo === "factory" ? "ri-building-2-line" : warehouseInfo === "overseas" ? "ri-global-line" : "ri-checkbox-multiple-line"} text-amber-400`}></i>
                    {warehouseInfo === "factory" ? "Products in Factory" : warehouseInfo === "overseas" ? "Overseas Warehouse Available" : "Factory + Overseas Warehouse"}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1.5 flex items-center gap-1">
                    <i className="ri-whatsapp-line text-green-500"></i> WhatsApp
                  </label>
                  {editMode ? (
                    <input type="text" value={whatsapp} onChange={e => setWhatsapp(e.target.value)} className="w-full border border-amber-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                  ) : (
                    <div className="bg-gray-50 rounded-xl px-3 py-2.5 text-sm text-gray-700">{whatsapp}</div>
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1.5 flex items-center gap-1">
                    <i className="ri-mail-line text-amber-400"></i> Business Email
                  </label>
                  {editMode ? (
                    <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} className="w-full border border-amber-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                  ) : (
                    <div className="bg-gray-50 rounded-xl px-3 py-2.5 text-sm text-gray-700">{contactEmail}</div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Containers Sold", value: "240+", icon: "ri-ship-line", color: "text-amber-500 bg-amber-50" },
                  { label: "Active Products", value: "1,240", icon: "ri-box-3-line", color: "text-emerald-500 bg-emerald-50" },
                  { label: "Buyer Inquiries", value: "3,800+", icon: "ri-message-3-line", color: "text-rose-500 bg-rose-50" },
                ].map(s => (
                  <div key={s.label} className="bg-gray-50 rounded-xl p-3 text-center">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg mx-auto mb-1.5 ${s.color}`}>
                      <i className={`${s.icon} text-sm`}></i>
                    </div>
                    <div className="font-extrabold text-gray-900 text-base">{s.value}</div>
                    <div className="text-xs text-gray-400">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Products Tab ── */}
          {activeTab === "products" && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-800">My Products &amp; FOB Prices</h3>
                <span className="text-xs text-gray-400">Click edit to update product info</span>
              </div>
              {products.map(p => (
                <div key={p.id} className="border border-gray-100 rounded-xl overflow-hidden">
                  {editingProduct === p.id ? (
                    <div className="p-4 flex flex-col gap-3">
                      <div className="flex gap-3">
                        <img src={p.image} alt={p.name} className="w-16 h-16 rounded-lg object-cover object-top shrink-0" />
                        <div className="flex-1 flex flex-col gap-2">
                          <input value={editProductName} onChange={e => setEditProductName(e.target.value)} className="border border-amber-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400" placeholder="Product name" />
                          <div className="flex gap-2">
                            <input value={editProductPrice} onChange={e => setEditProductPrice(e.target.value)} className="flex-1 border border-amber-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400" placeholder="FOB Price" />
                            <select value={editProductStock} onChange={e => setEditProductStock(e.target.value)} className="flex-1 border border-amber-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer">
                              <option>In Factory</option>
                              <option>Overseas Warehouse</option>
                              <option>Both</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => saveProduct(p.id)} className="flex-1 bg-amber-400 hover:bg-amber-500 text-white text-xs font-bold py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"><i className="ri-save-line mr-1"></i>Save</button>
                        <button onClick={() => setEditingProduct(null)} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-bold py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-3">
                      <img src={p.image} alt={p.name} className="w-14 h-14 rounded-lg object-cover object-top shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 text-sm truncate">{p.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-amber-600 font-bold text-xs">{p.price}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${p.stock === "Overseas Warehouse" ? "bg-emerald-100 text-emerald-600" : "bg-amber-100 text-amber-600"}`}>
                            <i className={`${p.stock === "Overseas Warehouse" ? "ri-global-line" : "ri-building-2-line"} mr-1`}></i>{p.stock}
                          </span>
                        </div>
                      </div>
                      <button onClick={() => startEditProduct(p)} className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-amber-100 text-gray-500 hover:text-amber-600 rounded-lg transition-colors cursor-pointer">
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                    </div>
                  )}
                </div>
              ))}
              <button className="w-full border-2 border-dashed border-gray-200 hover:border-amber-300 text-gray-400 hover:text-amber-500 rounded-xl py-4 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-add-line text-lg"></i> Add New Product
              </button>
            </div>
          )}

          {/* ── Inbox Tab ── */}
          {activeTab === "inbox" && (
            <div className="flex flex-col gap-0 h-full">
              {!selectedInquiry ? (
                <>
                  {/* Inbox header */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-gray-800">Buyer Inquiries</h3>
                      <p className="text-xs text-gray-400 mt-0.5">{unreadCount} unread · {inquiries.length} total</p>
                    </div>
                    <div className="flex gap-1.5">
                      {(["all", "unread", "replied"] as const).map(f => (
                        <button
                          key={f}
                          onClick={() => setInboxFilter(f)}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap capitalize ${inboxFilter === f ? "bg-amber-400 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Inquiry list */}
                  <div className="flex flex-col gap-2">
                    {filteredInquiries.map(inq => (
                      <button
                        key={inq.id}
                        onClick={() => openInquiry(inq)}
                        className={`w-full text-left rounded-xl p-4 border transition-all cursor-pointer hover:border-amber-300 hover:bg-amber-50/30 ${!inq.read ? "border-amber-200 bg-amber-50/50" : "border-gray-100 bg-white"}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="relative shrink-0">
                            <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-amber-100 to-orange-100 rounded-full text-lg">
                              {inq.flag}
                            </div>
                            {!inq.read && (
                              <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-0.5">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className={`font-bold text-sm truncate ${!inq.read ? "text-gray-900" : "text-gray-700"}`}>{inq.buyer}</span>
                                <span className="text-xs text-gray-400 shrink-0">{inq.company}</span>
                              </div>
                              <span className="text-xs text-gray-400 shrink-0">{inq.time}</span>
                            </div>
                            <p className="text-xs text-amber-600 font-semibold mb-1 truncate">
                              <i className="ri-box-3-line mr-1"></i>{inq.product} · {inq.quantity}
                            </p>
                            <p className="text-xs text-gray-500 line-clamp-1">{inq.message}</p>
                            <div className="flex items-center gap-2 mt-1.5">
                              {inq.replied && (
                                <span className="text-xs bg-green-100 text-green-600 font-semibold px-2 py-0.5 rounded-full">
                                  <i className="ri-reply-line mr-0.5"></i>Replied
                                </span>
                              )}
                              {!inq.read && (
                                <span className="text-xs bg-red-100 text-red-500 font-semibold px-2 py-0.5 rounded-full">New</span>
                              )}
                              <span className="text-xs text-gray-400">{inq.budget}</span>
                            </div>
                          </div>
                          <i className="ri-arrow-right-s-line text-gray-300 shrink-0 mt-1"></i>
                        </div>
                      </button>
                    ))}

                    {filteredInquiries.length === 0 && (
                      <div className="text-center py-10 text-gray-400">
                        <i className="ri-inbox-line text-4xl mb-2 block"></i>
                        <p className="text-sm">No {inboxFilter === "all" ? "" : inboxFilter} inquiries</p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                /* ── Inquiry Detail / Chat ── */
                <div className="flex flex-col h-full">
                  {/* Back + header */}
                  <div className="flex items-center gap-3 mb-4">
                    <button
                      onClick={() => setSelectedInquiry(null)}
                      className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer shrink-0"
                    >
                      <i className="ri-arrow-left-line text-gray-600 text-sm"></i>
                    </button>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-xl">{selectedInquiry.flag}</span>
                      <div className="min-w-0">
                        <p className="font-bold text-gray-800 text-sm truncate">{selectedInquiry.buyer} · {selectedInquiry.company}</p>
                        <p className="text-xs text-gray-400">{selectedInquiry.country} · {selectedInquiry.time}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <a
                        href={`https://wa.me/${selectedInquiry.whatsapp.replace(/\D/g, "")}`}
                        target="_blank"
                        rel="nofollow noreferrer"
                        className="w-8 h-8 flex items-center justify-center bg-green-100 hover:bg-green-200 text-green-600 rounded-lg transition-colors cursor-pointer"
                      >
                        <i className="ri-whatsapp-line text-sm"></i>
                      </a>
                      <a
                        href={`mailto:${selectedInquiry.email}`}
                        className="w-8 h-8 flex items-center justify-center bg-amber-100 hover:bg-amber-200 text-amber-600 rounded-lg transition-colors cursor-pointer"
                      >
                        <i className="ri-mail-line text-sm"></i>
                      </a>
                    </div>
                  </div>

                  {/* Inquiry details */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                    <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                      <div><span className="text-gray-400">Product: </span><span className="font-semibold text-gray-800">{selectedInquiry.product}</span></div>
                      <div><span className="text-gray-400">Quantity: </span><span className="font-semibold text-gray-800">{selectedInquiry.quantity}</span></div>
                      <div><span className="text-gray-400">Budget: </span><span className="font-semibold text-amber-700">{selectedInquiry.budget}</span></div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">Contact: </span>
                        <span className="text-green-600 font-semibold">{selectedInquiry.whatsapp}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-700 leading-relaxed bg-white rounded-lg px-3 py-2.5 border border-amber-100">
                      {selectedInquiry.message}
                    </p>
                  </div>

                  {/* Chat thread */}
                  {selectedInquiry.replies.length > 0 && (
                    <div className="flex flex-col gap-3 mb-4">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Conversation</p>
                      {selectedInquiry.replies.map((r, i) => (
                        <div key={i} className={`flex ${r.from === "factory" ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-xs leading-relaxed ${r.from === "factory" ? "bg-amber-400 text-white rounded-br-sm" : "bg-gray-100 text-gray-700 rounded-bl-sm"}`}>
                            <p>{r.text}</p>
                            <p className={`text-[10px] mt-1 ${r.from === "factory" ? "text-amber-100" : "text-gray-400"}`}>{r.time}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reply success */}
                  {replySent && (
                    <div className="bg-green-50 border border-green-200 text-green-700 text-xs rounded-xl px-4 py-2.5 mb-3 flex items-center gap-2">
                      <i className="ri-checkbox-circle-fill"></i> Reply sent! The buyer will be notified.
                    </div>
                  )}

                  {/* Reply box */}
                  <div className="mt-auto">
                    <p className="text-xs font-semibold text-gray-500 mb-2">Reply to Buyer</p>
                    <div className="flex flex-col gap-2">
                      {/* Quick reply templates */}
                      <div className="flex gap-2 flex-wrap">
                        {[
                          "Thank you for your inquiry! Our FOB price is competitive. Please share your WhatsApp.",
                          "We have this item in stock. Ready to ship within 3–5 days.",
                          "Please send us your full specifications and we will prepare a detailed quotation.",
                        ].map((t, i) => (
                          <button
                            key={i}
                            onClick={() => setReplyText(t)}
                            className="text-xs bg-gray-50 hover:bg-amber-50 border border-gray-200 hover:border-amber-300 text-gray-600 hover:text-amber-700 px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                          >
                            {i === 0 ? "Price reply" : i === 1 ? "In stock" : "Need specs"}
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <textarea
                          value={replyText}
                          onChange={e => { if (e.target.value.length <= 500) setReplyText(e.target.value); }}
                          rows={3}
                          placeholder="Type your reply to the buyer..."
                          className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 resize-none"
                          maxLength={500}
                        />
                        <button
                          onClick={sendReply}
                          disabled={!replyText.trim()}
                          className="w-12 flex items-center justify-center bg-amber-400 hover:bg-amber-500 disabled:opacity-40 text-white rounded-xl transition-colors cursor-pointer shrink-0"
                        >
                          <i className="ri-send-plane-fill text-lg"></i>
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400">{replyText.length}/500</span>
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          <span>Or contact directly:</span>
                          <a href={`https://wa.me/${selectedInquiry.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="nofollow noreferrer" className="flex items-center gap-1 text-green-600 font-semibold hover:text-green-700 cursor-pointer">
                            <i className="ri-whatsapp-line"></i> WhatsApp
                          </a>
                          <a href={`mailto:${selectedInquiry.email}`} className="flex items-center gap-1 text-amber-600 font-semibold hover:text-amber-700 cursor-pointer">
                            <i className="ri-mail-line"></i> Email
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Contact Tab ── */}
          {activeTab === "contact" && (
            <div className="flex flex-col gap-5">
              <h3 className="font-bold text-gray-800">Contact &amp; Exchange Info with Buyer</h3>
              {msgSent && (
                <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill"></i> Message sent! The buyer will receive your contact info.
                </div>
              )}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm font-bold text-gray-800 mb-3">Share Your Contact with Buyer</p>
                <div className="flex gap-2 mb-3">
                  <button onClick={() => setContactMethod("whatsapp")} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border transition-all cursor-pointer whitespace-nowrap ${contactMethod === "whatsapp" ? "border-green-400 bg-green-50 text-green-600" : "border-gray-200 bg-white text-gray-500"}`}>
                    <i className="ri-whatsapp-line text-green-500"></i> WhatsApp
                  </button>
                  <button onClick={() => setContactMethod("email")} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border transition-all cursor-pointer whitespace-nowrap ${contactMethod === "email" ? "border-amber-400 bg-amber-50 text-amber-600" : "border-gray-200 bg-white text-gray-500"}`}>
                    <i className="ri-mail-line text-amber-500"></i> Email
                  </button>
                </div>
                <div className="bg-white rounded-lg px-3 py-2.5 text-sm text-gray-700 border border-gray-200 mb-3">
                  {contactMethod === "whatsapp" ? whatsapp : contactEmail}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-2 block">Send Message to Buyer</label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} placeholder="Hello! I saw your requirement and I can supply this product..." className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 resize-none" maxLength={500} />
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-gray-400">{message.length}/500</span>
                  <button onClick={handleSendMessage} disabled={!message.trim()} className="bg-amber-400 hover:bg-amber-500 disabled:opacity-50 text-white text-xs font-bold px-5 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-send-plane-line mr-1.5"></i>Send Message
                  </button>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">Quick Message Templates</p>
                <div className="flex flex-col gap-2">
                  {["Hi! I can supply this product. Please share your WhatsApp for quick communication.", "We have this item in stock at our overseas warehouse. Ready to ship within 3 days.", "Our FOB price is competitive. Please contact me for a detailed quotation."].map((t, i) => (
                    <button key={i} onClick={() => setMessage(t)} className="text-left text-xs text-gray-600 bg-gray-50 hover:bg-amber-50 hover:text-amber-700 border border-gray-100 hover:border-amber-200 rounded-lg px-3 py-2.5 transition-colors cursor-pointer">{t}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Donate Tab ── */}
          {activeTab === "donate" && (
            <div className="flex flex-col gap-5">
              <div className="relative rounded-2xl overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=beautiful%20diverse%20babies%20infants%20smiling%20warm%20soft%20light%20hopeful%20emotional%20photography%20love%20care%20world%20touching&width=600&height=200&seq=donate1&orientation=landscape" alt="Infants" className="w-full h-40 object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-white font-extrabold text-lg">Help Infants Who Need Love</p>
                  <p className="text-white/80 text-xs">Milk, clothes, and medical care for babies worldwide</p>
                </div>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-rose-100 rounded-xl shrink-0">
                    <i className="ri-ship-line text-rose-500 text-xl"></i>
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 text-sm">Big Seller? Make a Big Impact!</p>
                    <p className="text-gray-500 text-xs mt-1 leading-relaxed">If your factory sells many containers and has been blessed with success, consider making a donation to help infants in need around the world.</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[{ label: "Infants Helped", value: "12,800+", icon: "ri-emotion-happy-line", color: "text-rose-500 bg-rose-50" }, { label: "Countries", value: "48", icon: "ri-global-line", color: "text-amber-500 bg-amber-50" }, { label: "Total Raised", value: "$284K", icon: "ri-heart-fill", color: "text-emerald-500 bg-emerald-50" }].map(s => (
                  <div key={s.label} className="bg-gray-50 rounded-xl p-3 text-center">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg mx-auto mb-1.5 ${s.color}`}><i className={`${s.icon} text-sm`}></i></div>
                    <div className="font-extrabold text-gray-900 text-sm">{s.value}</div>
                    <div className="text-xs text-gray-400">{s.label}</div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-xs font-semibold text-gray-600">Choose Donation Amount</p>
                <div className="grid grid-cols-4 gap-2">
                  {["$10", "$50", "$100", "$500"].map(amt => (
                    <button key={amt} onClick={onDonate} className="bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-600 font-bold text-sm py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap">{amt}</button>
                  ))}
                </div>
              </div>
              <button onClick={onDonate} className="w-full bg-rose-500 hover:bg-rose-600 text-white font-extrabold py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-heart-fill text-lg"></i>Donate Now — Help Infants Worldwide
              </button>
              <p className="text-center text-xs text-gray-400 leading-relaxed">100% of donations go directly to infant care programs — milk, warm clothes, and medical support for babies in need.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
